import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import ListPagination from "@/components/ListPagination";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { unwrapPaginated } from "@/lib/pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import RichTextEditor from "@/components/RichTextEditor";
import { Workflow, Plus, Trash2, Edit3, Clock, Mail, BarChart3, GitBranch, Tag } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmDialogContext";

const TRIGGERS = [
  { value: "signup", label: "Contact signs up" },
  { value: "tag_added", label: "Tag is added to contact" },
  { value: "manual", label: "Manually enrolled" },
];

const STEP_TYPES = [
  { value: "send", label: "Send email", icon: Mail },
  { value: "wait", label: "Wait", icon: Clock },
  { value: "condition", label: "Condition (branch)", icon: GitBranch },
  { value: "add_tag", label: "Add tag", icon: Tag },
  { value: "remove_tag", label: "Remove tag", icon: Tag },
];

const CONDITION_TYPES = [
  { value: "opened_previous", label: "Opened a previous email" },
  { value: "clicked_previous", label: "Clicked in a previous email" },
  { value: "has_tag", label: "Has tag" },
];

const emptyStep = (type = "send") => {
  const base = { type, delay_hours: 0 };
  if (type === "send") return { ...base, subject: "", content: "<p>Hi there,</p>" };
  if (type === "condition") return { ...base, condition_type: "opened_previous", condition_value: "", yes_next: null, no_next: -1 };
  if (type === "add_tag" || type === "remove_tag") return { ...base, tag: "" };
  return base;
};

export default function WorkflowsPage() {
  const confirm = useConfirm();
  const [audiences, setAudiences] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [activeStep, setActiveStep] = useState(0);

  const fetcher = useCallback((page, limit) => api.get("/workflows", { params: { page, limit } }), []);

  const {
    items,
    total,
    pages,
    page,
    limit,
    setPage,
    setLimit,
    loading,
    reload,
  } = usePaginatedList(fetcher, []);

  useEffect(() => {
    api.get("/audiences").then((r) => setAudiences(unwrapPaginated(r.data).items));
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({
      name: "", audience_id: audiences[0]?.id || "",
      trigger_type: "signup", trigger_value: "",
      from_name: "", from_email: "", reply_to: "",
      is_active: true, steps: [emptyStep()],
    });
    setActiveStep(0);
    setOpen(true);
  };

  const openEdit = async (w) => {
    setEditing(w);
    setForm({
      name: w.name, audience_id: w.audience_id,
      trigger_type: w.trigger_type, trigger_value: w.trigger_value || "",
      from_name: w.from_name, from_email: w.from_email, reply_to: w.reply_to || "",
      is_active: w.is_active, steps: w.steps,
    });
    setActiveStep(0);
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.audience_id || !form.steps.length) return toast.error("Name, audience and at least one step required");
    for (const s of form.steps) {
      if (s.type === "send" && !s.subject) return toast.error("Every send step needs a subject");
      if ((s.type === "add_tag" || s.type === "remove_tag") && !s.tag) return toast.error("Tag steps need a tag value");
    }
    try {
      const payload = { ...form, trigger_value: form.trigger_value || null };
      if (editing) {
        await api.put(`/workflows/${editing.id}`, payload);
        toast.success("Workflow updated");
      } else {
        await api.post("/workflows", payload);
        toast.success("Workflow created");
      }
      setOpen(false);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  const showAnalytics = async (w) => {
    try {
      const { data } = await api.get(`/workflows/${w.id}/analytics`);
      setAnalytics({ ...data, name: w.name });
      setAnalyticsOpen(true);
    } catch (err) {
      toast.error("Failed to load analytics");
    }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete workflow?",
      description: "Delete this workflow and all enrolled runs? This cannot be undone.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/workflows/${id}`);
    toast.success("Deleted");
    reload();
  };

  const addStep = () => {
    setForm({ ...form, steps: [...form.steps, emptyStep()] });
    setActiveStep(form.steps.length);
  };

  const removeStep = (idx) => {
    if (form.steps.length === 1) return;
    const next = form.steps.filter((_, i) => i !== idx);
    setForm({ ...form, steps: next });
    setActiveStep(Math.min(activeStep, next.length - 1));
  };

  const patchStep = (idx, patch) => {
    const next = [...form.steps];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, steps: next });
  };

  return (
    <div data-testid="workflows-page">
      <PageHeader
        title="Automations"
        subtitle="Drip sequences that send when contacts trigger an event."
        action={
          <Button onClick={openNew} className="rounded-sm h-11 btn-brand font-semibold" data-testid="new-workflow-button">
            <Plus className="h-4 w-4 mr-2" /> New automation
          </Button>
        }
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        {loading ? (
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading...</div>
        ) : total === 0 ? (
          <div className="lift-card bg-white dark:bg-neutral-950 p-12 rounded-sm text-center">
            <Workflow className="h-10 w-10 text-neutral-300 mx-auto mb-4" />
            <h3 className="font-display font-bold text-xl mb-2">No automations yet</h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">Set up a welcome series or onboarding drip in minutes.</p>
            <Button onClick={openNew} className="rounded-sm btn-brand font-semibold">
              <Plus className="h-4 w-4 mr-2" /> Create automation
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((w) => (
              <div key={w.id} className="lift-card bg-white dark:bg-neutral-950 p-6 rounded-sm" data-testid={`workflow-card-${w.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="h-10 w-10 rounded-sm flex items-center justify-center" style={{ backgroundColor: "var(--bg-muted)" }}>
                    <Workflow className="h-5 w-5 text-neutral-700 dark:text-neutral-300" />
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-bold uppercase tracking-wider ${w.is_active ? "bg-emerald-50 text-emerald-800" : "bg-neutral-100 text-neutral-600 dark:text-neutral-400"}`}>
                    {w.is_active ? "Active" : "Off"}
                  </span>
                </div>
                <h3 className="font-display font-bold text-xl tracking-tight mb-2">{w.name}</h3>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">Trigger: {TRIGGERS.find((t) => t.value === w.trigger_type)?.label} {w.trigger_value && `(${w.trigger_value})`}</p>
                <div className="flex items-center gap-4 text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                  <span><Mail className="h-3.5 w-3.5 inline mr-1" /> {w.steps.length} step{w.steps.length === 1 ? "" : "s"}</span>
                  <span>{w.enrolled} enrolled</span>
                  <span>{w.completed} completed</span>
                </div>
                <div className="flex gap-2 pt-4 border-t border-neutral-100 dark:border-neutral-800">
                  <Button size="sm" variant="outline" className="rounded-sm flex-1" onClick={() => showAnalytics(w)} data-testid={`workflow-analytics-${w.id}`}>
                    <BarChart3 className="h-3 w-3 mr-1" /> Analytics
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-sm" onClick={() => openEdit(w)} data-testid={`workflow-edit-${w.id}`}>
                    <Edit3 className="h-3 w-3" />
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-sm text-red-600" onClick={() => remove(w.id)} data-testid={`workflow-delete-${w.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        {!loading && total > 0 && (
          <ListPagination
            page={page}
            pages={pages}
            total={total}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={setLimit}
            className="mt-4 rounded-sm border border-neutral-200 dark:border-neutral-800"
          />
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit automation" : "New automation"}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-5 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-sm" placeholder="Welcome series" data-testid="workflow-name-input" />
                </div>
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <Label className="text-xs font-bold uppercase tracking-wider">Audience</Label>
                    <Select value={form.audience_id} onValueChange={(v) => setForm({ ...form, audience_id: v })}>
                      <SelectTrigger className="mt-2 rounded-sm" data-testid="workflow-audience-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{audiences.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 pb-2">
                    <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="workflow-active-toggle" />
                    <span className="text-sm">Active</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Trigger</Label>
                  <Select value={form.trigger_type} onValueChange={(v) => setForm({ ...form, trigger_type: v })}>
                    <SelectTrigger className="mt-2 rounded-sm" data-testid="workflow-trigger-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{TRIGGERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {form.trigger_type === "tag_added" && (
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider">Tag</Label>
                    <Input value={form.trigger_value} onChange={(e) => setForm({ ...form, trigger_value: e.target.value })} className="mt-2 rounded-sm" placeholder="vip" data-testid="workflow-trigger-value-input" />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">From name</Label>
                  <Input value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} className="mt-2 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">From email</Label>
                  <Input type="email" value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })} className="mt-2 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Reply-to</Label>
                  <Input type="email" value={form.reply_to} onChange={(e) => setForm({ ...form, reply_to: e.target.value })} className="mt-2 rounded-sm" />
                </div>
              </div>

              <div className="border-t border-neutral-200 dark:border-neutral-800 pt-5">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-xs font-bold uppercase tracking-wider mb-0">Steps</Label>
                  <Button size="sm" variant="outline" className="rounded-sm" onClick={addStep} data-testid="workflow-add-step">
                    <Plus className="h-3 w-3 mr-1" /> Add step
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {form.steps.map((s, idx) => (
                    <div key={idx} className="relative">
                      <button onClick={() => setActiveStep(idx)} className={`px-3 py-2 rounded-sm border text-xs font-bold transition-colors ${activeStep === idx ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-200 dark:border-neutral-800 hover:border-neutral-400"}`} data-testid={`workflow-step-tab-${idx}`}>
                        Step {idx + 1}
                        {s.delay_hours > 0 && <span className="ml-2 opacity-75">+{s.delay_hours}h</span>}
                      </button>
                      {form.steps.length > 1 && (
                        <button onClick={() => removeStep(idx)} className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-600 text-white text-xs flex items-center justify-center">×</button>
                      )}
                    </div>
                  ))}
                </div>

                {form.steps[activeStep] && (
                  <div className="space-y-3 border border-neutral-200 dark:border-neutral-800 rounded-sm p-4 bg-neutral-50 dark:bg-neutral-900/50">
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider">Step type</Label>
                      <Select value={form.steps[activeStep].type} onValueChange={(v) => { const ns = [...form.steps]; ns[activeStep] = emptyStep(v); setForm({ ...form, steps: ns }); }}>
                        <SelectTrigger className="mt-2 rounded-sm" data-testid={`workflow-step-type-${activeStep}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{STEP_TYPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider flex items-center gap-1"><Clock className="h-3 w-3" /> Delay (hours)</Label>
                      <Input type="number" min="0" value={form.steps[activeStep].delay_hours} onChange={(e) => patchStep(activeStep, { delay_hours: parseInt(e.target.value) || 0 })} className="mt-2 rounded-sm" data-testid={`workflow-step-delay-${activeStep}`} />
                    </div>

                    {form.steps[activeStep].type === "send" && (
                      <>
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider">Subject</Label>
                          <Input value={form.steps[activeStep].subject || ""} onChange={(e) => patchStep(activeStep, { subject: e.target.value })} className="mt-2 rounded-sm" data-testid={`workflow-step-subject-${activeStep}`} />
                        </div>
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider">Content</Label>
                          <div className="mt-2">
                            <RichTextEditor value={form.steps[activeStep].content || ""} onChange={(html) => patchStep(activeStep, { content: html })} placeholder="Write the email body..." />
                          </div>
                        </div>
                      </>
                    )}

                    {form.steps[activeStep].type === "condition" && (
                      <>
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider">Condition</Label>
                          <Select value={form.steps[activeStep].condition_type || "opened_previous"} onValueChange={(v) => patchStep(activeStep, { condition_type: v })}>
                            <SelectTrigger className="mt-2 rounded-sm" data-testid={`workflow-condition-type-${activeStep}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{CONDITION_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        {form.steps[activeStep].condition_type === "has_tag" && (
                          <div>
                            <Label className="text-xs font-bold uppercase tracking-wider">Tag</Label>
                            <Input value={form.steps[activeStep].condition_value || ""} onChange={(e) => patchStep(activeStep, { condition_value: e.target.value })} className="mt-2 rounded-sm" placeholder="vip" />
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs font-bold uppercase tracking-wider">If YES → step</Label>
                            <Input type="number" value={form.steps[activeStep].yes_next ?? ""} onChange={(e) => patchStep(activeStep, { yes_next: e.target.value === "" ? null : parseInt(e.target.value) })} className="mt-2 rounded-sm" placeholder={`${activeStep + 1} (next)`} data-testid={`workflow-yes-next-${activeStep}`} />
                          </div>
                          <div>
                            <Label className="text-xs font-bold uppercase tracking-wider">If NO → step</Label>
                            <Input type="number" value={form.steps[activeStep].no_next ?? -1} onChange={(e) => patchStep(activeStep, { no_next: e.target.value === "" ? null : parseInt(e.target.value) })} className="mt-2 rounded-sm" placeholder="-1 (end)" data-testid={`workflow-no-next-${activeStep}`} />
                          </div>
                        </div>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">Use -1 to end workflow. Empty = continue to next step.</p>
                      </>
                    )}

                    {(form.steps[activeStep].type === "add_tag" || form.steps[activeStep].type === "remove_tag") && (
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider">Tag</Label>
                        <Input value={form.steps[activeStep].tag || ""} onChange={(e) => patchStep(activeStep, { tag: e.target.value })} className="mt-2 rounded-sm" placeholder="vip" data-testid={`workflow-tag-${activeStep}`} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} className="rounded-sm btn-brand font-semibold" data-testid="workflow-save-submit">{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={analyticsOpen} onOpenChange={setAnalyticsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{analytics?.name} — Analytics</DialogTitle></DialogHeader>
          {analytics && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-3 gap-3">
                <div className="border border-neutral-200 dark:border-neutral-800 rounded-sm p-3">
                  <div className="label-overline mb-1">Enrolled</div>
                  <div className="text-2xl font-display font-black">{analytics.enrolled}</div>
                </div>
                <div className="border border-neutral-200 dark:border-neutral-800 rounded-sm p-3">
                  <div className="label-overline mb-1">Active</div>
                  <div className="text-2xl font-display font-black">{analytics.active}</div>
                </div>
                <div className="border border-neutral-200 dark:border-neutral-800 rounded-sm p-3">
                  <div className="label-overline mb-1">Completed</div>
                  <div className="text-2xl font-display font-black">{analytics.completed}</div>
                </div>
              </div>
              <div className="space-y-2">
                {analytics.steps.map((s) => (
                  <div key={s.step_index} className="border border-neutral-200 dark:border-neutral-800 rounded-sm p-4 text-sm" data-testid={`analytics-step-${s.step_index}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="label-overline">Step {s.step_index + 1} · {s.type}</span>
                    </div>
                    {s.type === "send" && <div className="font-semibold mb-2">{s.subject}</div>}
                    <div className="grid grid-cols-4 gap-2 text-xs">
                      <div><span className="text-neutral-500 dark:text-neutral-400">Sent</span><br /><strong>{s.sent}</strong></div>
                      <div><span className="text-neutral-500 dark:text-neutral-400">Opens</span><br /><strong>{s.unique_opens}</strong></div>
                      <div><span className="text-neutral-500 dark:text-neutral-400">Clicks</span><br /><strong>{s.unique_clicks}</strong></div>
                      <div><span className="text-neutral-500 dark:text-neutral-400">Open rate</span><br /><strong>{s.sent ? Math.round((s.unique_opens / s.sent) * 100) : 0}%</strong></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setAnalyticsOpen(false)} className="rounded-sm btn-brand font-semibold">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
